import { Link } from "react-router-dom";
import { Package, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Checkbox } from "@/components/ui/checkbox";
import { ProductThumb } from "@/components/products/ProductThumb";
import { FavoriteStar } from "@/components/products/FavoriteStar";
import { isNewProduct } from "@/lib/productBadges";
import { getProductTypeName } from "@/lib/productTypes";

// factory cost display — respects the entry currency (USD when entered as such, else RMB)
const factoryCostCell = (r) => {
  const usd = r.factory_cost_usd || 0;
  const isUsd = r.cost_currency === "USD" || (!r.cost_currency && usd > 0);
  const val = isUsd ? usd : (r.factory_cost || 0);
  if (!val) return "-";
  return isUsd ? `$${val.toFixed(2)}` : `¥${val.toFixed(2)}`;
};

export const ProductsGrid = ({ products, loading, selected, onToggleSelect, readOnly = false, noEdit = false, currency = "USD", canSelect = null, density = "comfortable" }) => {
  const { t, lang } = useLanguage();
  const priceKey = currency === "CAD" ? "display_price_cad" : "display_price_usd";
  const sym = currency === "CAD" ? "C$" : "US$";
  // 2026-02-09 · Compact mode packs 6-8 cards per row and drops the price
  // table for a scannable "gallery" style. Comfortable keeps full detail.
  const isCompact = density === "compact";
  // Customer role case: readOnly is TRUE (no click-through / no factory-cost),
  // yet customer STILL needs the selection checkbox to build a Set-Tag or
  // Grid-PDF batch (2026-02 spec). ``canSelect`` overrides when passed.
  const showCheckbox = canSelect !== null ? canSelect : !readOnly;
  // Body wrapper for the product card. Customers can't click into the edit page
  // (spec 2026-02 「客户不需要点击这个产品进入产品的编辑」), so we render a plain div
  // instead of a react-router <Link>. Staff/factory still get the drill-through.
  const CardBody = ({ item, children }) => (
    noEdit ? (
      <div className="block">{children}</div>
    ) : (
      <Link to={`/products/${encodeURIComponent(item)}/edit`} className="block">{children}</Link>
    )
  );

  return (
    <div
      className={`grid ${isCompact
        ? "grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-8 gap-2"
        : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"}`}
      data-testid="products-grid"
      data-density={density}
    >
      {!loading && products.length === 0 && (
        <p className="col-span-full text-center py-10 text-muted-foreground" data-testid="products-empty-state">
          {t("products.noProducts")}
        </p>
      )}
      {products.map((p) => (
        <div
          key={p.item_number}
          className="group relative bg-card border border-border rounded-md overflow-hidden hover:border-brand transition-colors"
          data-testid={`product-card-${p.item_number}`}
        >
          {showCheckbox && (
          <div className="absolute top-9 left-2 z-10 p-1 rounded-md bg-white/95 border border-border" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected?.has(p.item_number) || false}
              onCheckedChange={() => onToggleSelect?.(p.item_number)}
              data-testid={`grid-product-select-${p.item_number}`}
              className="block"
            />
          </div>
          )}
          {/* Favorite star — top-right corner (was the quick-add-to-quote
              button pre-2026-02; user asked to convert it to 加入我的收藏 with
              the corresponding icon). */}
          <div className="absolute top-9 right-2 z-10 p-1 rounded-md bg-white/95 border border-border shadow-sm" onClick={(e) => e.stopPropagation()}>
            <FavoriteStar itemNumber={p.item_number} initial={p.is_favorite} size={14} />
          </div>
          {isNewProduct(p) && (
            <span
              className="absolute top-9 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-400 text-black text-[10px] font-bold uppercase tracking-wide shadow-sm"
              data-testid={`product-new-badge-${p.item_number}`}
            >
              <Sparkles size={10} /> {t("products.newBadge")}
            </span>
          )}
          {/* 2026-06 · 产品类型徽章 — 图片上方独立一条，用户要求（可多选，逐个显示） */}
          <div className="h-7 px-2 flex items-center gap-1 overflow-hidden border-b border-border bg-secondary/40">
            {((p.product_types || []).length ? p.product_types : (p.product_type ? [p.product_type] : [])).length ? (
              ((p.product_types || []).length ? p.product_types : [p.product_type]).map((code, i) => (
                <span
                  key={code}
                  className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-brand/10 text-brand text-[10px] font-semibold leading-none max-w-full truncate shrink-0"
                  data-testid={i === 0 ? `product-type-badge-${p.item_number}` : `product-type-badge-${p.item_number}-${code}`}
                >
                  {getProductTypeName(code, lang)}
                </span>
              ))
            ) : (
              <span className="text-[10px] text-muted-foreground/50" data-testid={`product-type-badge-empty-${p.item_number}`}>—</span>
            )}
          </div>
          <CardBody item={p.item_number}>
            <div className="aspect-square bg-white border-b border-border flex items-center justify-center overflow-hidden p-2">
              <ProductThumb
                photoUrl={p.photo_url}
                patternPhotoUrl={p.pattern_photo_url}
                compositePhotoUrl={p.composite_photo_url}
                alt={p.item_number}
                size="md"
                side="bottom"
                className="w-full h-full"
                fallbackIconSize={32}
              />
            </div>
            <div className={isCompact ? "p-1.5" : "p-2.5"}>
              <div className="flex items-center gap-1.5 min-w-0">
                <p className={`font-mono font-bold truncate ${isCompact ? "text-[11px]" : "text-[13px]"}`}>{p.item_number}</p>
                {p.tag && !isCompact && (
                  <span className="shrink-0 px-1.5 py-0.5 rounded-sm bg-brand/10 text-brand text-[10px] font-medium leading-none max-w-[70px] truncate" data-testid={`product-tag-badge-${p.item_number}`}>
                    {p.tag}
                  </span>
                )}
              </div>
              {!isCompact && (
                <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2 min-h-[28px]">{p.description || p.description_zh || "-"}</p>
              )}
              {/* Specification (spec 2026-02): shown under description when present.
                  Especially valuable for products that only have a single size row
                  where the spec-table is otherwise sparse. */}
              {!isCompact && (p.specification || p.specification_zh) && (
                <p className="mt-1 text-[10px] text-foreground/70 leading-snug line-clamp-2" data-testid={`product-spec-${p.item_number}`}>
                  <span className="font-medium">{t("priceList.specification")}:</span> {p.specification || p.specification_zh}
                </p>
              )}
              {!isCompact && (p.price_lists || []).length > 0 && (
                <table className="w-full mt-2 font-mono text-[10px]">
                  <thead>
                    <tr className="text-muted-foreground uppercase tracking-wider border-b border-border">
                      <th className="text-left font-medium pb-0.5">{t("priceList.size")}</th>
                      {/* Factory Cost column hidden for read-only roles (factory / customer)
                          per spec 2026-02 「去掉 FACTORY COST」on customer product cards. */}
                      {!readOnly && (
                        <th className="text-right font-medium pb-0.5">{t("priceList.factoryCost")}</th>
                      )}
                      <th className="text-right font-medium pb-0.5 text-brand">{sym}</th>
                      {/* 2026-10-22 · 用户要求去掉「40 尺柜装箱量」列（空间太挤且卡片一级信息里不需要） */}
                    </tr>
                  </thead>
                  <tbody>
                    {p.price_lists.slice(0, 4).map((r, ri) => (
                      <tr key={r.id || ri} className="border-b border-border/50 last:border-0">
                        <td className="py-0.5 truncate max-w-[60px]">{r.size || "-"}</td>
                        {!readOnly && (
                          <td className="py-0.5 text-right whitespace-nowrap" data-testid={`grid-price-rmb-${p.item_number}-${ri}`}>
                            {factoryCostCell(r)}
                          </td>
                        )}
                        <td className="py-0.5 text-right whitespace-nowrap text-brand font-bold" data-testid={`grid-price-${p.item_number}-${ri}`}>
                          {r[priceKey] ? `${sym}${r[priceKey].toFixed(2)}` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardBody>
        </div>
      ))}
    </div>
  );
};
;
